import React from 'react'
import Image from 'next/image'
import Button from '../ui/Button'
import ProgramActions from './ProgramActions'
import RichText from '../Common/RichText'
import type { IFormationDetail } from '@/types/formation'
import { LEVEL_LABELS } from '@/constants/formation'
import { formatFrenchDate } from '@/lib/utils'
import { getNextSessionByType } from '@/lib/formation'
import {
  Layers,
  Users,
} from "lucide-react";

interface SummaryBarProps {
    formation: IFormationDetail;
    className?: string;
}

/** One white card of the summary row. */
const SummaryCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="bg-white rounded-xl shadow-[1px_2px_8px_2px_#0000001A] p-4 md:p-5 wide:p-4 mt-8">
        {children}
    </div>
)

const SummaryBar: React.FC<SummaryBarProps> = ({ formation, className = "" }) => {
    const moduleCount = formation.program.modules.length
    const remoteSession = formation?.sessions?.find(
        s => s.session_type === "elearning" || s.session_type === "distanciel"
    );
    const nextOnsiteSession = getNextSessionByType(formation.sessions, ['presentiel'])
    const prerequisitesHtml = formation.prerequisites ?? "<p>Aucun prérequis spécifique.</p>"

    return (
    <div className={`w-full relative ${className}`}>
        <div className="fiche-summary-grid text-left">

            {/* Card 1 — Modules */}
            <SummaryCard>
                <div className="flex items-center gap-3.5">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EDEDED]">
                        <Layers className="w-5.5 h-5.5 text-[#1B365E] shrink-0" aria-hidden="true" />
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                        <p className="text-[13px] text-[#1B515E] leading-4">Modules</p>
                        <p className="font-bold text-[17px] text-[#1D1D1D] leading-5.5 whitespace-nowrap">
                            {moduleCount > 0 ? `${moduleCount} modules` : `${formation.duration_hours}h`}
                        </p>
                    </div>
                </div>
            </SummaryCard>

            {/* Card 2 — Niveau + prérequis */}
            <SummaryCard>
                <div className="flex items-start gap-3.5">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EDEDED]">
                        <Users className="w-5.5 h-5.5 text-[#1B365E] shrink-0" aria-hidden="true" />
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                        <p className="text-[13px] text-[#1B515E] leading-4">Niveau</p>
                        <p className="font-bold text-[17px] text-[#1D1D1D] leading-5.5">
                            {LEVEL_LABELS[formation.level] ?? formation.level}
                        </p>
                    </div>
                    <RichText
                        html={prerequisitesHtml}
                        className="md:block hidden min-w-0 flex-1 border-l border-[#E3E3E3] pl-4 text-[12px] text-[#1D1D1D] leading-4.5 wrap-break-word"
                    />
                </div>
                <RichText
                    html={prerequisitesHtml}
                    className="md:hidden block mt-3 text-[12px] text-[#1D1D1D] leading-4.5 wrap-break-word"
                />
            </SummaryCard>

            {/* Card 3 — Certification */}
            <SummaryCard>
                <div className="flex items-center gap-3.5">
                    <Image
                        className="w-9 shrink-0"
                        src="/assets/icons/certificate-icon.svg"
                        alt="Certification"
                        title="Certification"
                        aria-hidden="true"
                        width={37}
                        height={34}
                    />
                    <div className="flex flex-col gap-1 min-w-0">
                        <p className="text-[13px] text-[#1B515E] leading-4">Certification</p>
                        <p
                            title={formation.has_certification ? (formation.certification_name ?? undefined) : undefined}
                            className="font-bold text-[15px] text-[#1D1D1D] leading-5.5 line-clamp-2"
                        >
                            {formation.has_certification
                                ? (formation.certification_name ?? "Certificat inclus")
                                : "Non certifiante"}
                        </p>
                    </div>
                </div>
            </SummaryCard>

            {/* Card 4 — Prochaines sessions */}
            <SummaryCard>
                <p className="font-bold text-[17px] text-[#1D1D1D] leading-5.5 mb-3 whitespace-nowrap">Prochaines sessions</p>
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                        <span className="shrink-0 rounded-full bg-[#CFF1F9] text-[#1B515E] text-[11px] leading-4 px-2.5 py-1">
                            Présentiel
                        </span>
                        <p className="text-[13px] text-[#1D1D1D] leading-4.5">
                            {nextOnsiteSession ? formatFrenchDate(nextOnsiteSession.start_date) : "À programmer"}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="shrink-0 rounded-full bg-[#EDEDED] text-[#1D1D1D] text-[11px] leading-4 px-2.5 py-1">
                            Distanciel
                        </span>
                        <p className="text-[13px] text-[#1D1D1D] leading-4.5">
                            {remoteSession ? formatFrenchDate(remoteSession.start_date) : "Non disponible"}
                        </p>
                    </div>
                </div>
                <a
                    href="#sessions"
                    title="Voir les sessions"
                    className="md:hidden inline-flex items-center min-h-11 mt-1 text-[#1B365E] font-bold text-[14px] hover:opacity-70 transition-opacity"
                >
                    Voir les sessions
                </a>
            </SummaryCard>
        </div>

        {/* Mobile action icons — save, print, download & share the programme */}
        <ProgramActions
            formation={formation}
            className="md:hidden flex justify-center mt-6"
        />

        {/* Height came from `h-9` while the padding asked for 58px, so the label
            overflowed its own background. One height, one target size. */}
        <Button
            href="#sessions"
            variant="gradient"
            size="unset"
            className="md:hidden w-full h-12 rounded-lg text-base leading-6 font-bold px-6 mt-6"
        >
            Postuler à cette formation
        </Button>
    </div>
  )
}

export default SummaryBar